import { useLayoutEffect, useRef } from 'react'

/**
 * useHeightMorph — плавный морф высоты контейнера при смене содержимого
 * (Artlist-эффект: виджет «растягивается» под новый блок и «сжимается»
 * обратно). Замерено на toolkit.artlist.io: открытие ~400ms, закрытие
 * ~200ms, деселерирующая кривая.
 *
 * FLIP по высоте: на смену `dep` layout-эффект берёт СТАРУЮ высоту
 * (запомненную после прошлого рендера), новая уже в DOM → фиксируем старую
 * инлайном, форсим reflow и переводим в новую с transition. По окончании
 * снимаем инлайн (height: auto — контент снова управляет размером).
 *
 * ВАЖНО (урок): при БЫСТРОМ повторном переключении dep прежний морф может
 * быть прерван до таймера очистки — тогда inline height/overflow «залипали»
 * и панель оставалась растянутой (виджет «уезжал» вверх). Поэтому:
 *   1) замер натуральной высоты всегда идёт ПОСЛЕ сброса возможных inline
 *      от прерванного морфа;
 *   2) cleanup эффекта снимает inline СИНХРОННО (не полагаясь на таймер).
 *
 * Чистая презентация: не трогает состояние/логику, только style контейнера.
 */
export function useHeightMorph(
	ref: React.RefObject<HTMLElement | null>,
	dep: unknown,
	opts?: { grow?: number; shrink?: number },
) {
	const prevHeight = useRef<number | null>(null)
	const prevDep = useRef(dep)
	const cleanupTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

	useLayoutEffect(() => {
		const el = ref.current
		if (!el) return

		const depChanged = prevDep.current !== dep
		prevDep.current = dep

		// Сбрасываем inline прерванного морфа ДО замера — иначе offsetHeight
		// вернёт зафиксированную анимацией высоту вместо натуральной.
		const clearInline = () => {
			el.style.transition = ''
			el.style.height = ''
			el.style.overflow = ''
		}
		if (el.style.height !== '') {
			el.style.transition = 'none'
			el.style.height = ''
			el.style.overflow = ''
			void el.offsetHeight
			el.style.transition = ''
		}

		const next = el.offsetHeight
		const prev = prevHeight.current
		prevHeight.current = next

		if (!depChanged || prev == null || prev === next) return

		// Открытие (рост) — медленнее, закрытие (сжатие) — быстрее (Artlist).
		const growing = next > prev
		const dur = growing ? (opts?.grow ?? 420) : (opts?.shrink ?? 240)

		if (cleanupTimer.current) clearTimeout(cleanupTimer.current)
		el.style.transition = 'none'
		el.style.height = `${prev}px`
		el.style.overflow = 'hidden'
		void el.offsetHeight // reflow: зафиксировать стартовую высоту
		el.style.transition = `height ${dur}ms cubic-bezier(0.22, 1, 0.36, 1)`
		el.style.height = `${next}px`

		cleanupTimer.current = setTimeout(() => {
			cleanupTimer.current = null
			clearInline()
			// Финальная высота могла измениться за время анимации (ленивые
			// картинки и т.п.) — перезапомним фактическую.
			prevHeight.current = el.offsetHeight
		}, dur + 60)

		return () => {
			// Прерывание (смена dep до конца морфа / размонтирование):
			// снимаем inline СРАЗУ — ничего не должно «залипнуть».
			if (cleanupTimer.current) {
				clearTimeout(cleanupTimer.current)
				cleanupTimer.current = null
				clearInline()
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dep])
}
